/**
 * 账号与密码。
 *
 * 此前管理端**没有任何改密码入口** —— 启动日志与 `.env.example` 都写着
 * 「请立即登录并修改」，但既无路由也无页面，密码一旦建号就再也改不了
 * （`admin_users.password_hash` 只在首次建号时写入过一次）。
 */
import { useState } from 'react'
import { KeyRound, Loader2, ShieldCheck } from 'lucide-react'
import { toast } from 'sonner'
import { api, setToken } from '@/lib/api'
import { roleLabel, useAuth } from '@/lib/auth'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
/** 与服务端 `POST /api/auth/password` 的校验保持一致。 */
const MIN_LENGTH = 8

export function AccountPage() {
  const { user, role, roleName, capabilities } = useAuth()
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [busy, setBusy] = useState(false)

  const tooShort = newPassword.length > 0 && newPassword.length < MIN_LENGTH
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword
  const canSubmit =
    !busy &&
    oldPassword.length > 0 &&
    newPassword.length >= MIN_LENGTH &&
    newPassword === confirmPassword

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setBusy(true)
    try {
      const res = await api.changePassword(oldPassword, newPassword)
      // 服务端会作废旧令牌并补发新的，必须立刻换上，否则下一次请求就 401
      setToken(res.token)
      toast.success('密码已修改')
      setOldPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '修改失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">账号与密码</h1>
      </div>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="size-4" />
            当前身份
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-[var(--color-muted-foreground)]">账号</span>
            <span className="font-medium">{user?.username ?? '—'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[var(--color-muted-foreground)]">角色</span>
            <Badge variant={role === 'admin' ? 'default' : 'outline'}>
              {roleLabel(role, roleName)}
            </Badge>
          </div>
          <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3 text-xs leading-relaxed text-[var(--color-muted-foreground)]">
            当前角色「{roleName}」拥有 {capabilities.length}{' '}
            项权限。具体能力由管理员在「角色与权限」页维护。
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="size-4" />
            修改密码
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="old-password">当前密码</Label>
              <Input
                id="old-password"
                type="password"
                autoComplete="current-password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-password">新密码</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              {tooShort && (
                <p className="text-xs text-[var(--color-destructive)]">
                  新密码至少 {MIN_LENGTH} 位
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">确认新密码</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              {mismatch && (
                <p className="text-xs text-[var(--color-destructive)]">两次输入不一致</p>
              )}
            </div>
            <Button type="submit" disabled={!canSubmit}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
              修改密码
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
